import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';
import { forwardRef, useCallback, useState } from 'react';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import { HotelFilters } from './HotelFilters';
import type { TransitionProps } from '@mui/material/transitions';
import type { ReactElement, Ref } from 'react';
import type { HotelFilterState } from '../types/hotels.presentation';
import type { MealOption, HotelTypeOption } from '../constants/hotelMasterData';

interface MobileHotelFiltersProps {
  filters: HotelFilterState;
  onFiltersChange: (filters: HotelFilterState) => void;
  priceBounds: [number, number];
  mealOptions?: MealOption[];
  hotelTypeOptions?: HotelTypeOption[];
  mealCounts?: Record<string, number>;
  hotelTypeCounts?: Record<number, number>;
  hasPromotionsAvailable?: boolean;
}

const Transition = forwardRef(
  (props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) => {
    return <Slide direction='up' ref={ref} {...props} />;
  },
);

export function MobileHotelFilters({
  filters,
  onFiltersChange,
  priceBounds,
  mealOptions,
  hotelTypeOptions,
  mealCounts,
  hotelTypeCounts,
  hasPromotionsAvailable,
}: MobileHotelFiltersProps) {
  const [open, setOpen] = useState(false);
  const { background } = useListingColors();

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClear = useCallback(() => {
    onFiltersChange({});
  }, [onFiltersChange]);

  return (
    <>
      <button
        aria-label='Abrir filtros'
        className='flex items-center justify-center gap-2.5 px-5 py-2.5'
        onClick={handleToggle}
        style={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#f5f5f5',
          borderRadius: '25px',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 10000,
        }}
        type='button'
      >
        {open ? <CloseIcon /> : <TuneIcon />}
        <p>Filtros</p>
      </button>

      <Dialog
        TransitionComponent={Transition}
        disableEnforceFocus
        disableScrollLock={false}
        fullScreen
        onClose={handleClose}
        open={open}
        slotProps={{
          backdrop: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        <AppBar sx={{ position: 'relative', backgroundColor: background }}>
          <Toolbar>
            <IconButton aria-label='close' color='inherit' edge='start' onClick={handleClose}>
              <CloseIcon />
            </IconButton>
            <Typography component='div' sx={{ ml: 2, flex: 1 }} variant='h6'>
              Filtros
            </Typography>
            <button
              aria-label='Limpiar filtros'
              className='rounded-md px-2 py-1 text-sm text-gray-700 transition-colors hover:bg-white/10'
              onClick={handleClear}
              type='button'
            >
              Limpiar
            </button>
          </Toolbar>
        </AppBar>
        <DialogContent
          sx={{
            overflowY: 'scroll',
            maxHeight: '90vh',
            padding: 0,
          }}
        >
          <HotelFilters
            filters={filters}
            hasPromotionsAvailable={hasPromotionsAvailable}
            hotelTypeCounts={hotelTypeCounts}
            hotelTypeOptions={hotelTypeOptions}
            mealCounts={mealCounts}
            mealOptions={mealOptions}
            onFiltersChange={onFiltersChange}
            priceBounds={priceBounds}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
